// Atajos de limpieza + filtros avanzados de la página /almacenamiento.
import React from "react";
import { Button, DatePicker, Input, Select, Tooltip } from "antd";
import type { Dayjs } from "dayjs";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ClearOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import type {
  DireccionOrden,
  EstadoFiltro,
  OrdenArchivos,
  TipoArchivo,
} from "../../../services/almacenamiento.api";
import {
  ANTIGUEDADES_MESES,
  ATAJOS,
  ETIQUETA_ESTADO,
  ETIQUETA_ORDEN,
  ETIQUETA_TIPO_PLURAL,
  ORDENES_VALIDOS,
  PESOS_MIN_MB,
  TIPOS_VALIDOS,
  atajoActivo,
  etiquetaAntiguedad,
  etiquetaPesoMin,
  type FiltrosArchivos,
  type IdAtajo,
} from "../almacenamientoUtils";
import type { OpcionPropiedad } from "../useAlmacenamientoLogic";

const { RangePicker } = DatePicker;

export interface PanelFiltrosProps {
  filtros: FiltrosArchivos;
  hayFiltros: boolean;
  textoBusqueda: string;
  onTextoBusqueda: (v: string) => void;
  onBuscar: (v: string | undefined) => void;
  /** Opciones de módulo tomadas del resumen (id + etiqueta). */
  modulos: Array<{ value: string; label: string }>;
  propiedades: OpcionPropiedad[];
  rango: [Dayjs | null, Dayjs | null] | null;
  onRango: (r: [Dayjs | null, Dayjs | null] | null) => void;
  onModulo: (v: string | undefined) => void;
  onTipo: (v: TipoArchivo | undefined) => void;
  onPropiedad: (v: number | undefined) => void;
  onAntiguedad: (v: number | undefined) => void;
  onPesoMin: (v: number | undefined) => void;
  onEstado: (v: EstadoFiltro) => void;
  onOrden: (v: OrdenArchivos) => void;
  onDir: (v: DireccionOrden) => void;
  onAtajo: (id: IdAtajo) => void;
  onLimpiar: () => void;
}

const PanelFiltros: React.FC<PanelFiltrosProps> = ({
  filtros,
  hayFiltros,
  textoBusqueda,
  onTextoBusqueda,
  onBuscar,
  modulos,
  propiedades,
  rango,
  onRango,
  onModulo,
  onTipo,
  onPropiedad,
  onAntiguedad,
  onPesoMin,
  onEstado,
  onOrden,
  onDir,
  onAtajo,
  onLimpiar,
}) => {
  const activo = atajoActivo(filtros);

  return (
    <section className="alm-filtros" aria-label="Filtros de archivos">
      <div className="alm-atajos" role="group" aria-label="Atajos de limpieza">
        <span className="alm-atajos-titulo">
          <ThunderboltOutlined /> Atajos de limpieza
        </span>
        {ATAJOS.map((a) => (
          <Tooltip key={a.id} title={a.descripcion}>
            <Button
              className="alm-btn-tactil alm-atajo"
              type={activo === a.id ? "primary" : "default"}
              aria-pressed={activo === a.id}
              onClick={() => onAtajo(a.id)}
            >
              {a.etiqueta}
            </Button>
          </Tooltip>
        ))}
      </div>

      <div className="alm-filtros-grid">
        <Input.Search
          className="alm-filtro-busqueda"
          allowClear
          placeholder="Buscar por nombre, dueño, propiedad o quién lo subió"
          aria-label="Buscar archivos"
          value={textoBusqueda}
          onChange={(e) => {
            const v = e.target.value;
            onTextoBusqueda(v);
            // Al vaciar la caja (X o borrar todo) se quita el filtro sin Enter.
            if (!v) onBuscar(undefined);
          }}
          onSearch={(v) => onBuscar(v.trim() || undefined)}
        />
        <Select
          allowClear
          showSearch
          placeholder="Módulo"
          aria-label="Filtrar por módulo"
          value={filtros.modulo}
          onChange={(v) => onModulo(v ?? undefined)}
          options={modulos}
          optionFilterProp="label"
          notFoundContent="Sin módulos"
        />
        <Select
          allowClear
          placeholder="Tipo de archivo"
          aria-label="Filtrar por tipo"
          value={filtros.tipo}
          onChange={(v) => onTipo(v ?? undefined)}
          options={TIPOS_VALIDOS.map((t) => ({ value: t, label: ETIQUETA_TIPO_PLURAL[t] }))}
        />
        <Select
          allowClear
          showSearch
          placeholder="Propiedad"
          aria-label="Filtrar por propiedad"
          value={filtros.propiedadId}
          onChange={(v) => onPropiedad(v ?? undefined)}
          options={propiedades.map((p) => ({ value: p.value, label: p.label, nombre: p.nombre }))}
          filterOption={(texto, opcion) =>
            String((opcion as { nombre?: string })?.nombre ?? opcion?.label ?? "")
              .toLowerCase()
              .includes(texto.toLowerCase())
          }
          notFoundContent="Sin propiedades"
        />
        <RangePicker
          className="alm-filtro-rango"
          format="DD-MM-YYYY"
          value={rango as [Dayjs, Dayjs] | null}
          placeholder={["Subido desde", "Subido hasta"]}
          onChange={(vals) => onRango(vals && vals[0] && vals[1] ? [vals[0], vals[1]] : null)}
          allowClear
        />
        <Select
          allowClear
          placeholder="Antigüedad"
          aria-label="Filtrar por antigüedad"
          value={filtros.antiguedadMeses}
          onChange={(v) => onAntiguedad(v ?? undefined)}
          options={ANTIGUEDADES_MESES.map((m) => ({ value: m, label: etiquetaAntiguedad(m) }))}
        />
        <Select
          allowClear
          placeholder="Peso mínimo"
          aria-label="Filtrar por peso mínimo"
          value={filtros.pesoMinMB}
          onChange={(v) => onPesoMin(v ?? undefined)}
          options={PESOS_MIN_MB.map((mb) => ({ value: mb, label: etiquetaPesoMin(mb) }))}
        />
        <Select
          placeholder="Estado"
          aria-label="Filtrar por estado"
          value={filtros.estado}
          onChange={(v) => onEstado(v)}
          options={(Object.keys(ETIQUETA_ESTADO) as EstadoFiltro[]).map((e) => ({
            value: e,
            label: e === "todos" ? "Estado: todos" : ETIQUETA_ESTADO[e],
          }))}
        />
        <div className="alm-orden">
          <Select
            aria-label="Ordenar por"
            value={filtros.orden}
            onChange={(v) => onOrden(v)}
            options={ORDENES_VALIDOS.map((o) => ({
              value: o,
              label: `Ordenar por ${ETIQUETA_ORDEN[o].toLowerCase()}`,
            }))}
            style={{ flex: 1, minWidth: 0 }}
          />
          <Tooltip title={filtros.dir === "asc" ? "Ascendente" : "Descendente"}>
            <Button
              className="alm-btn-tactil"
              icon={filtros.dir === "asc" ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              aria-label={`Dirección del orden: ${filtros.dir === "asc" ? "ascendente" : "descendente"}`}
              onClick={() => onDir(filtros.dir === "asc" ? "desc" : "asc")}
            />
          </Tooltip>
        </div>
        <Button
          className="alm-btn-tactil"
          icon={<ClearOutlined />}
          onClick={onLimpiar}
          disabled={!hayFiltros}
        >
          Limpiar filtros
        </Button>
      </div>
    </section>
  );
};

export default PanelFiltros;
